import React, { useState, useEffect } from 'react';
import { ArrowLeft, Banknote, CreditCard, Clock, Camera, X, Plus } from 'lucide-react';
import { useFinancial } from '../../../../contexts/FinancialContext';
import ConfirmationSlider from '../../../../components/ConfirmationSlider';
import NumericKeypad from '../../../../components/NumericKeypad';

// Temporal: Esto debería venir de un contexto o prop
const STAFF = [
  { id: 1, name: 'Juan', color: 'bg-blue-500' },
  { id: 2, name: 'María', color: 'bg-purple-500' },
  { id: 3, name: 'Carlos', color: 'bg-orange-500' },
  { id: 4, name: 'Ana', color: 'bg-green-500' },
];

const PaymentMethodButton = ({ icon: Icon, label, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`flex-1 p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
      selected 
        ? 'border-green-500 bg-green-50 dark:bg-green-900/20 scale-[1.02] shadow-lg' 
        : 'border-gray-200 dark:border-gray-700 hover:border-green-500/50 hover:bg-green-50/50 dark:hover:bg-green-900/10'
    }`}
  >
    <Icon className={`w-8 h-8 ${selected ? 'text-green-500' : 'text-gray-500 dark:text-gray-400'}`} />
    <span className={`text-base font-medium ${selected ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-300'}`}>
      {label}
    </span>
  </button>
);

const PaymentModal = ({ isOpen, onClose, onConfirm, totalAmount, saleId }) => {
  const { currentClosing, registerSalePayment } = useFinancial();
  const [payments, setPayments] = useState([]);
  const [currentPayment, setCurrentPayment] = useState({
    method: '',
    amount: '',
    bank: '',
    receipt: null
  });
  const [selectedCashier, setSelectedCashier] = useState('1');
  const [canConfirmPayment, setCanConfirmPayment] = useState(false);
  const [error, setError] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(STAFF[0]); // Default to first staff member
  const [showKeypad, setShowKeypad] = useState(false);
  const [inputConfirmed, setInputConfirmed] = useState(false);
  const [otherMethods, setOtherMethods] = useState(() => {
    const saved = localStorage.getItem('otherPaymentMethods');
    return saved ? JSON.parse(saved) : [];
  });
  const [newMethodName, setNewMethodName] = useState('');
  const [showNewMethodInput, setShowNewMethodInput] = useState(false);

  const remainingAmount = totalAmount - payments.reduce((sum, payment) => sum + Number(payment.amount), 0);

  useEffect(() => {
    setCanConfirmPayment(remainingAmount === 0 && (payments.length > 0 || inputConfirmed));
  }, [remainingAmount, payments.length, inputConfirmed]);

  const handleMethodChange = (method, otherMethodName = '') => {
    // Si el método ya está seleccionado, lo deseleccionamos
    if (method === currentPayment.method) {
      setCurrentPayment({
        method: '',
        amount: '',
        bank: '',
        receipt: null,
        otherMethodName: ''
      });
      setInputConfirmed(false);
      return;
    }

    if (method === 'credit') {
      // Si es fiado, siempre establecer el monto total restante
      const newPayment = {
        method: 'credit',
        amount: remainingAmount.toString(),
        bank: '',
        receipt: null,
        otherMethodName
      };

      // Si no hay pagos previos, agregar directamente el pago
      if (payments.length === 0) {
        setPayments([newPayment]);
        setCurrentPayment({
          method: '',
          amount: '',
          bank: '',
          receipt: null,
          otherMethodName
        });
        setInputConfirmed(true);
      } else {
        // Si hay pagos previos, actualizar el current payment
        setCurrentPayment(newPayment);
      }
    } else {
      setCurrentPayment({
        ...currentPayment,
        method,
        otherMethodName,
        bank: '',
        receipt: null
      });
      setInputConfirmed(false);
    }
    setError('');
  };

  const handleAmountInput = (value) => {
    const newAmount = currentPayment.amount + value.toString();
    if (Number(newAmount) <= remainingAmount) {
      setCurrentPayment({
        ...currentPayment,
        amount: newAmount
      });
      setError('');
    }
  };

  const handleAmountDelete = () => {
    setCurrentPayment({
      ...currentPayment,
      amount: currentPayment.amount.slice(0, -1)
    });
  };

  const handleSetTotalAmount = () => {
    const newPayment = {
      ...currentPayment,
      amount: remainingAmount.toString()
    };
    setCurrentPayment(newPayment);
    setPayments([...payments, newPayment]);
  };

  const handleBankChange = (e) => {
    setCurrentPayment({
      ...currentPayment,
      bank: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCurrentPayment({
        ...currentPayment,
        receipt: file
      });
    }
  };

  const handleAddPayment = () => {
    if (!currentPayment.method || !currentPayment.amount) {
      setError('Por favor complete el método de pago y el monto');
      return;
    }

    setPayments([...payments, currentPayment]);
    setCurrentPayment({
      method: '',
      amount: '',
      bank: '',
      receipt: null,
      otherMethodName: ''
    });
    setError('');
    setShowKeypad(false);
    setInputConfirmed(true);
  };

  const handleConfirm = async () => {
    try {
      if (!currentClosing) {
        setError('No hay un cierre de caja abierto. Por favor abra la caja antes de registrar pagos.');
        return;
      }

      // Registrar el pago en el sistema financiero
      const transactions = await registerSalePayment(saleId, {
        payments,
        staffId: selectedStaff.id,
        staffName: selectedStaff.name
      });

      // Si llegamos aquí, el pago se registró correctamente
      onConfirm({
        payments,
        cashier: selectedCashier,
        staffId: selectedStaff.id,
        staffName: selectedStaff.name,
        transactions
      });
    } catch (error) {
      console.error('Error al registrar el pago:', error);
      setError('Error al registrar el pago. Por favor intente nuevamente.');
    }
  };

  const handleDeletePayment = (index) => {
    setPayments(payments.filter((_, i) => i !== index));
    setInputConfirmed(false);
  };

  const handleAddOtherMethod = () => {
    if (newMethodName.trim()) {
      const updatedMethods = [...otherMethods, newMethodName.trim()];
      setOtherMethods(updatedMethods);
      localStorage.setItem('otherPaymentMethods', JSON.stringify(updatedMethods));
      handleMethodChange('other', newMethodName.trim());
      setNewMethodName('');
      setShowNewMethodInput(false);
    }
  };

  // Determinar si toda la venta es a fiado
  const isEntireSaleCredit = payments.length === 1 && 
    payments[0]?.method === 'credit' && 
    Number(payments[0]?.amount) === totalAmount;

  if (!saleId) {
    console.error('PaymentModal: saleId is required');
    return null;
  }

  if (!isOpen) return null;

  if (!currentClosing) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose}>
        <div className="fixed inset-x-0 bottom-0 bg-white dark:bg-gray-800 rounded-t-xl p-4" onClick={e => e.stopPropagation()}>
          <div className="flex flex-col items-center gap-4">
            <X className="w-8 h-8 text-red-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">No hay caja abierta</h2>
            <p className="text-center text-gray-500 dark:text-gray-400">
              Para registrar pagos, primero debe abrir la caja.
            </p>
            <button
              onClick={onClose}
              className="w-full py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose}>
      <div className="fixed inset-x-0 bottom-0 bg-white dark:bg-gray-800 rounded-t-xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="relative bg-gray-50 dark:bg-gray-700 rounded-t-xl">
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Registrar Pago</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Venta #{saleId}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-gray-900 dark:text-white">${totalAmount}</span>
                <button 
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="p-4 space-y-6">
            {/* Caja selector - Moved up */}
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">Caja</label>
              <select
                value={selectedCashier}
                onChange={(e) => setSelectedCashier(e.target.value)}
                className="w-full p-2 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="1">Caja 1</option>
                <option value="2">Caja 2</option>
              </select>
            </div>

            {/* Staff selector */}
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                RESPONSABLE DE COBRO
              </p>
              <div className="flex flex-wrap gap-3">
                {STAFF.map(staff => (
                  <button
                    key={staff.id}
                    onClick={() => setSelectedStaff(staff)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      selectedStaff?.id === staff.id
                        ? 'bg-gray-100 dark:bg-gray-700 ring-1 ring-gray-300 dark:ring-gray-600'
                        : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-base font-medium transition-all ${
                      selectedStaff?.id === staff.id
                        ? staff.color
                        : `${staff.color} opacity-50`
                    }`}>
                      {staff.name.charAt(0)}
                    </div>
                    <span className={`text-base font-medium transition-colors ${
                      selectedStaff?.id === staff.id
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {staff.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Payment method section */}
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">Método de Pago</label>
              <div className="grid grid-cols-3 gap-3">
                <PaymentMethodButton
                  icon={Banknote}
                  label="Efectivo"
                  selected={currentPayment.method === 'cash'}
                  onClick={() => handleMethodChange('cash')}
                />
                <PaymentMethodButton
                  icon={CreditCard}
                  label="Transferencia"
                  selected={currentPayment.method === 'transfer'}
                  onClick={() => handleMethodChange('transfer')}
                />
                <PaymentMethodButton
                  icon={Clock}
                  label="Fiado"
                  selected={currentPayment.method === 'credit'}
                  onClick={() => handleMethodChange('credit')}
                />
              </div>
              
              {/* Other payment methods */}
              <div className="mt-3">
                {otherMethods.map((method, index) => (
                  <button
                    key={index}
                    onClick={() => handleMethodChange('other', method)}
                    className={`mr-2 mb-2 px-3 py-2 rounded-lg text-sm ${
                      currentPayment.method === 'other' && currentPayment.otherMethodName === method
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {method}
                  </button>
                ))}
                <button
                  onClick={() => setShowNewMethodInput(true)}
                  className="px-3 py-2 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Otro
                </button>
              </div>

              {/* New method input */}
              {showNewMethodInput && (
                <div className="mt-3 flex gap-2">
                  <input
                    type="text"
                    value={newMethodName}
                    onChange={(e) => setNewMethodName(e.target.value)}
                    placeholder="Nombre del método de pago"
                    className="flex-1 p-2 border dark:border-gray-700 rounded-lg"
                    autoFocus
                  />
                  <button
                    onClick={handleAddOtherMethod}
                    disabled={!newMethodName.trim()}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg disabled:opacity-50"
                  >
                    Agregar
                  </button>
                </div>
              )}
            </div>

            {/* Transfer additional fields with animation */}
            {currentPayment.method === 'transfer' && (
              <div className="space-y-4 animate-fade-in bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">Banco</label>
                  <select
                    value={currentPayment.bank}
                    onChange={handleBankChange}
                    className="w-full p-2 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="">Seleccionar banco</option>
                    <option value="bank1">Banco 1</option>
                    <option value="bank2">Banco 2</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Comprobante (opcional)
                  </label>
                  <button
                    onClick={() => document.getElementById('receipt-upload').click()}
                    className="w-full p-4 border-2 border-dashed dark:border-gray-700 rounded-lg flex flex-col items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <Camera className="w-6 h-6 text-gray-400" />
                    <span className="text-sm text-gray-500">Subir foto del comprobante</span>
                  </button>
                  <input
                    id="receipt-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  {currentPayment.receipt && (
                    <div className="mt-2 text-sm text-green-500">
                      ✓ Comprobante cargado
                    </div>
                  )}
                </div>
              </div>
            )}

            {payments.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">Pagos Registrados:</h3>
                <div className="space-y-2">
                  {payments.map((payment, index) => (
                    <div key={index} className="flex items-center justify-between text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 p-2 rounded-lg">
                      <span>
                        {payment.method === 'cash' && 'Efectivo'}
                        {payment.method === 'transfer' && `Transferencia (${payment.bank})`}
                        {payment.method === 'credit' && 'Fiado'}
                        {payment.method === 'other' && payment.otherMethodName}
                        : ${payment.amount}
                      </span>
                      <button
                        onClick={() => handleDeletePayment(index)}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full"
                      >
                        <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Fixed bottom section */}
        <div className="border-t dark:border-gray-700">
          {/* New amount input section */}
          {remainingAmount > 0 && currentPayment.method && (
            <div className="p-4">
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    handleSetTotalAmount();
                    setInputConfirmed(true);
                  }}
                  disabled={!currentPayment.method}
                  className="p-3 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="text-sm text-gray-500 dark:text-gray-400">Cobrar total</span>
                  <div className="text-lg font-medium text-gray-900 dark:text-white">${remainingAmount}</div>
                </button>
                
                <button
                  onClick={() => setShowKeypad(true)}
                  className="p-3 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-left"
                >
                  <span className="text-sm text-gray-500 dark:text-gray-400">Monto parcial</span>
                  <div className="text-lg font-medium text-gray-900 dark:text-white">
                    {currentPayment.amount ? `$${currentPayment.amount}` : '$0'}
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Numeric keypad - only show when entering custom amount */}
          {remainingAmount > 0 && showKeypad && currentPayment.method && (
            <NumericKeypad
              onInput={handleAmountInput}
              onDelete={handleAmountDelete}
              onConfirm={handleAddPayment}
            />
          )}

          {/* Confirmation slider */}
          <div className="p-4">
            <div className={`${!canConfirmPayment ? 'opacity-50 pointer-events-none' : ''}`}>
              <ConfirmationSlider 
                onConfirm={handleConfirm}
                text={canConfirmPayment 
                  ? "Desliza para confirmar pago" 
                  : inputConfirmed 
                    ? `Falta cobrar $${remainingAmount}`
                    : "Confirma el monto con el teclado numérico"
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal; 